import { useState } from "react";
import BemIt, { addClass } from "@gtechdoodler/bem-it";
import NumericFormat from "react-number-format";

import Box from "../../layout/boxes/Box";
import Typography from "../../typography/Typography/Typography";
import useViewport from "../../../hooks/useViewport";
import ProxyBidDynamicLabel from "./ProxyBidDynamicLabel/ProxyBidDynamicLabel";
import ProxyBidNumberField from "./ProxyBidNumberField/ProxyBidNumberField";
import ProxyBidButton from "./ProxyBidButton/ProxyBidButton";
import { StyledProxyBidWrapper } from "./ProxyBid.styles";
import { ProxyBidProps, ProxyBidStatusOptions } from "./ProxyBid.types";
import { MainButtonRoleOptions } from "./ProxyBidButton/ProxyBidButton.types";

const proxyBidLabels = {
	default: "Proxy Bid",
	applied: "Proxy Bid applied",
	deleted: "Proxy Bid deleted",
	outbid: "Proxy Bid outbid",
	suspended: "Bid Suspended",
};

const PROXY_BID_THOUSANDS_SEPARATOR = ",";
const PROXY_BID_MOBILE_MIN_WIDTH = "224px";
const PROXY_BID_DESKTOP_MIN_WIDTH = "266px";

const ProxyBid = ({
	value,
	step = 100,
	min,
	max,
	lastBid,
	usersLastBid,
	bidSuspended,
	width = "100%",
	className,
	prefix = "£",
	onChange,
	onApply,
	onDelete,
	onReview,
	...props
}: ProxyBidProps) => {
	const block = new BemIt("ProxyBid");

	const [proxyBidStatus, setProxyBidStatus] =
		useState<ProxyBidStatusOptions>("default");

	const [mainButtonDisabled, setMainButtonDisabled] = useState<boolean>(false);
	const [deleteTriggered, setDeleteTriggered] = useState<boolean>(false);

	const viewport = useViewport();
	const isMobileDevice = ["xs", "sm"].includes(viewport?.currentSize);
	const proxyBidMinWidth = isMobileDevice
		? PROXY_BID_MOBILE_MIN_WIDTH
		: PROXY_BID_DESKTOP_MIN_WIDTH;

	const secondaryButtonLabel =
		bidSuspended || (value === usersLastBid && !deleteTriggered)
			? "Delete"
			: "Cancel";
	const shouldDisplaySecondaryButton =
		proxyBidStatus === "applied" || value > min || bidSuspended;

	const incorrectIncrement = value % step !== 0;
	const error =
		(lastBid && lastBid >= value) || incorrectIncrement || bidSuspended;
	const mainButtonRole: MainButtonRoleOptions = deleteTriggered
		? "delete"
		: bidSuspended
		? "review"
		: "apply";

	const handleApply = async () => {
		setMainButtonDisabled(true);
		const appliedSuccessfully = await onApply();

		if (appliedSuccessfully) {
			setProxyBidStatus("applied");
			setMainButtonDisabled(true);
			return;
		}

		setMainButtonDisabled(false);
	};

	const handleDelete = async () => {
		setMainButtonDisabled(true);
		const deletedSuccessfully = await onDelete();
		if (deletedSuccessfully) {
			setProxyBidStatus("deleted");
			setDeleteTriggered(false);
			setMainButtonDisabled(false);
			return;
		}
		setDeleteTriggered(false);
		setMainButtonDisabled(false);
	};

	const handleReview = async () => {
		setMainButtonDisabled(true);
		const bidReviewed = await onReview();
		if (bidReviewed) {
			setProxyBidStatus("default");
			setMainButtonDisabled(false);
			return;
		}
	};

	const handleChange = (value: number) => {
		if (proxyBidStatus === "deleted") {
			setProxyBidStatus("default");
		}
		setMainButtonDisabled(false);
		onChange(value);
	};

	const handleSecondaryButtonClick = () => {
		if (mainButtonDisabled) {
			setMainButtonDisabled(false);
		}
		if (bidSuspended || (value === usersLastBid && !deleteTriggered)) {
			setDeleteTriggered(true);
			return;
		}
		if (deleteTriggered) {
			if (usersLastBid) {
				onChange(usersLastBid);
			}
			setProxyBidStatus("applied");
			setMainButtonDisabled(true);
			setDeleteTriggered(false);
			return;
		}

		if (proxyBidStatus === "applied" && usersLastBid) {
			onChange(usersLastBid);
			return;
		}

		setProxyBidStatus("default");
		onChange(min);
	};

	const getProxyBidLabel = () => {
		if (bidSuspended) {
			return proxyBidLabels["suspended"];
		}

		if (lastBid && value <= lastBid) {
			return proxyBidLabels["outbid"];
		}

		if (usersLastBid && usersLastBid < value) {
			return (
				<NumericFormat
					value={usersLastBid}
					thousandSeparator={PROXY_BID_THOUSANDS_SEPARATOR}
					displayType="text"
					prefix={prefix}
					renderText={(value) => <span>{`Was ${value}`}</span>}
				/>
			);
		}

		return proxyBidLabels[proxyBidStatus];
	};

	const isMainButtonDisabled =
		(mainButtonDisabled && !bidSuspended) ||
		(typeof usersLastBid !== "undefined" &&
			usersLastBid === value &&
			!deleteTriggered &&
			!bidSuspended) ||
		(typeof lastBid !== "undefined" && value <= lastBid) ||
		incorrectIncrement;

	const getLabelVariant = () => {
		if (
			bidSuspended ||
			proxyBidStatus === "deleted" ||
			(lastBid && lastBid >= value)
		) {
			return "error";
		}
		if (proxyBidStatus === "applied" && value === usersLastBid) {
			return "success";
		}

		return "default";
	};

	const getErrorMessage = (value: string) =>
		incorrectIncrement ? (
			<Typography left={0} variant="body-small" color="semantic_error_700">
				{`Please bid in increments of ${value}`}
			</Typography>
		) : null;

	return (
		<Box
			className={addClass(className).after(block)}
			width={width}
			minWidth={proxyBidMinWidth}
			{...props}
		>
			<ProxyBidDynamicLabel
				label={getProxyBidLabel()}
				handleSecondaryButtonClick={handleSecondaryButtonClick}
				shouldDisplaySecondaryButton={shouldDisplaySecondaryButton}
				buttonLabel={secondaryButtonLabel}
				variant={getLabelVariant()}
			/>
			<StyledProxyBidWrapper viewport={viewport}>
				<ProxyBidNumberField
					value={value}
					min={min}
					max={max}
					step={step}
					onChange={handleChange}
					error={error}
					thousandSeparator={PROXY_BID_THOUSANDS_SEPARATOR}
					prefix={prefix}
					suspended={bidSuspended}
					controlsDisabled={deleteTriggered}
				/>
				<ProxyBidButton
					mainButtonDisabled={isMainButtonDisabled}
					onDelete={handleDelete}
					onApply={handleApply}
					onReview={handleReview}
					shouldReviewBid={bidSuspended}
					deleteTriggered={deleteTriggered}
					mainButtonRole={mainButtonRole}
				/>
			</StyledProxyBidWrapper>
			<NumericFormat
				value={step}
				thousandSeparator={PROXY_BID_THOUSANDS_SEPARATOR}
				displayType="text"
				prefix={prefix}
				renderText={getErrorMessage}
			/>
		</Box>
	);
};

export default ProxyBid;
